import { getV0UserMe, updateApiClientConfig } from '@bbr/api-client';
import { query as solidQuery, createAsync, redirect } from '@solidjs/router';
import { createContext, createEffect, Show, Suspense, useContext } from 'solid-js';
import { ErrorBoundary } from '#components/error-boundary.tsx';
import type { User } from '@bbr/api-client';
import type { AccessorWithLatest } from '@solidjs/router';
import type { ParentProps } from 'solid-js';

type CTX = AccessorWithLatest<User | null>;

const UserCtx = createContext<CTX>();

export function UserContext(props: ParentProps) {
	const user = createAsync(async () => await getLoggedInUser(), { deferStream: true });

	createEffect(() => {
		const locale = user()?.locale;
		if (locale) {
			updateApiClientConfig({
				headers: {
					'accept-language': locale,
				},
			});
		}
	});

	return (
		<Suspense>
			<ErrorBoundary
				fallback={(err) => {
					return (
						<div>
							<pre>{err?.toString()}</pre>
						</div>
					);
				}}
			>
				<Show when={user() !== undefined}>
					<UserCtx.Provider value={user as AccessorWithLatest<User | null>}>{props.children}</UserCtx.Provider>
				</Show>
			</ErrorBoundary>
		</Suspense>
	);
}

export function useUser() {
	const ctx = useContext(UserCtx);
	if (!ctx) {
		throw new Error('User context not initialized');
	}
	return ctx;
}

export function useUserRequired() {
	const ctx = useContext(UserCtx);
	if (!ctx) {
		throw new Error('User context not initialized');
	}
	return ctx as AccessorWithLatest<User>;
}

// Manually reimplement lazy/minimal cachedGet
const getLoggedInUser = solidQuery(async (): Promise<User | null> => {
	try {
		const user = await getV0UserMe();

		return user?.data || null;
	} catch {
		return null;
	}
}, getV0UserMe.key);

export const requireLoggedIn = solidQuery(async () => {
	const user = await getLoggedInUser();
	if (user === null) {
		const url = new URL(window.location.toString());
		if (!url.searchParams.get('redirect')) {
			url.searchParams.set('redirect', url.pathname);
		}
		return redirect(`/auth/login?${url.searchParams.toString()}`, { status: 401 });
	}
	return user;
}, 'require-logged-in-user');

export const requireLoggedOut = solidQuery(async (redirectPath?: string) => {
	const user = await getLoggedInUser();
	if (user) {
		return redirect(redirectPath ?? '/');
	}
	return null;
}, 'require-logged-out');
